// Dominoes on screen (desktop + phone): the join prompt, the lobby (with the
// 100 / 150 / 200 target picker), and the game — scores, the line of play
// with its two open ends, who holds how many tiles, your tiles, Pass / Draw.
// Tap a tile to play it; if it fits both ends, tap the end you want.
// Mounted by GameOverlay. VR uses DominoesVRPanel.

import React, { useEffect, useRef, useState } from 'react';
import { tableAction, handOf, turnDeadline, type TableState } from '../gameStore';
import { JoinPrompt, LobbyPanel, HelpButton, GameOverBlock, PanelError, TurnTimer, leaveTable } from '../kit/LobbyKit';
import {
  legalPlays, ends, partnerOf, sideOf, SEAT_NAMES, TARGETS, DEFAULT_TARGET,
  type DominoesState, type End, type Play,
} from '../../../../supabase/functions/games/dominoes.ts';
import { PIP_LAYOUT, PIP_R, tilePips } from './pips';
import type { OverlayProps } from '../types';
import './DominoesOverlay.css';

/** A domino as SVG. `vertical`: a on top; otherwise a on the left. */
export function DominoSvg({ a, b, vertical = false, size = 22 }: { a: number; b: number; vertical?: boolean; size?: number }) {
  const w = vertical ? size : size * 2, h = vertical ? size * 2 : size;
  const half = (n: number, ox: number, oy: number) => (PIP_LAYOUT[n] ?? []).map(([x, y], i) => (
    <circle key={`${ox}-${oy}-${i}`} cx={ox + x * size} cy={oy + y * size} r={PIP_R * size} />
  ));
  return (
    <svg className="gx-dm-svg" width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true">
      <rect x={0.5} y={0.5} width={w - 1} height={h - 1} rx={size * 0.16} className="gx-dm-body" />
      {vertical
        ? <line x1={size * 0.15} y1={size} x2={size * 0.85} y2={size} className="gx-dm-div" />
        : <line x1={size} y1={size * 0.15} x2={size} y2={size * 0.85} className="gx-dm-div" />}
      <g className="gx-dm-pips">{half(a, 0, 0)}{vertical ? half(b, 0, size) : half(b, size, 0)}</g>
    </svg>
  );
}

const nameOf = (st: TableState, i: number) => st.seats[i]?.name ?? SEAT_NAMES[i];

/** Lobby: first to 100 / 150 / 200. */
function TargetPicker({ pid, st, busy }: { pid: string; st: TableState; busy: boolean }) {
  const cur = Number(st.options?.target) || DEFAULT_TARGET;
  return (
    <span className="gx-dm-targets" role="group" aria-label="Play to">
      Play to
      {TARGETS.map((t) => (
        <button key={t} className={`gx-sp-btn gx-sp-btn--ghost gx-dm-target ${cur === t ? 'is-on' : ''}`} aria-pressed={cur === t}
          disabled={busy} onClick={() => void tableAction(pid, 'configure', { options: { target: t } })}>{t}</button>
      ))}
    </span>
  );
}

export function handSummary(g: DominoesState, st: TableState, seat: number): string {
  const lh = g.lastHand;
  if (!lh) return '';
  if (lh.side === null) return 'Blocked — tied, nobody scores';
  const mine = seat >= 0 && sideOf(g, seat) === lh.side;
  const who = lh.seat === seat ? 'You' : nameOf(st, lh.seat!);
  const what = lh.how === 'domino' ? `${who} dominoed` : `Blocked — ${who === 'You' ? 'you have' : `${who} has`} the fewest pips`;
  return `${what}: ${mine ? 'you score' : 'they score'} ${lh.points} (${lh.raw} pips)`;
}

export function DominoesOverlay(props: OverlayProps) {
  const { def, pid, entry, table, seat, onHelp } = props;
  const [picked, setPicked] = useState<string | null>(null);
  const strip = useRef<HTMLDivElement>(null);
  const st = table.state;
  const g = (st.g ?? null) as DominoesState | null;
  const hand = handOf(entry);
  const myTurn = !!g && st.phase === 'playing' && g.phase === 'playing' && g.turn === seat;
  const lastKey = g ? `${g.handNo}:${g.line.length}:${g.turn}` : '';
  useEffect(() => { setPicked(null); }, [lastKey]);
  // Keep the newest tile in view.
  useEffect(() => {
    const el = strip.current;
    if (!el || !g?.last || g.last.kind !== 'play') return;
    el.scrollLeft = g.last.end === 'left' ? 0 : el.scrollWidth;
  }, [lastKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (seat < 0) return <JoinPrompt {...props} />;

  if (st.phase === 'lobby') {
    const n = st.seats.filter(Boolean).length;
    return (
      <LobbyPanel {...props}
        seatLabel={(i) => `${SEAT_NAMES[i]}${i % 2 === seat % 2 && i !== seat ? ' · partner (4 players)' : ''}`}
        footer={<>
          <TargetPicker pid={pid} st={st} busy={entry.busy} />
          <span className="gx-dm-mode">{n === 4 ? '4 players: partners, N/S vs E/W.' : n === 2 ? '2 players: head-to-head with a boneyard.' : 'Seat 2 for head-to-head or 4 for partners.'}</span>
        </>} />
    );
  }
  if (!g) return null;

  const legal: Play[] = myTurn ? legalPlays(g, hand) : [];
  const fits = (t: string) => legal.filter((p) => p.tile === t).map((p) => p.end);
  const play = (tile: string, end?: End) => {
    setPicked(null);
    void tableAction(pid, 'move', { move: end ? { type: 'play', tile, end } : { type: 'play', tile } });
  };
  const tap = (t: string) => {
    const f = fits(t);
    if (!f.length || entry.busy) return;
    if (f.length === 1) play(t, f[0]);
    else setPicked((p) => (p === t ? null : t));
  };
  const pickedEnds = picked ? fits(picked) : [];
  const e = ends(g);
  const stuck = myTurn && legal.length === 0;
  const mySide = sideOf(g, seat);
  const partner = partnerOf(g, seat);

  let status: string;
  if (st.phase === 'over') status = g.winner === mySide ? 'You win the game!' : g.mode === 'partners' ? 'They win the game' : `${nameOf(st, g.sides[g.winner ?? 0][0])} wins the game`;
  else if (g.phase === 'hand_over') status = `${handSummary(g, st, seat)} — next hand soon`;
  else if (myTurn) status = g.line.length === 0 ? (g.mustLead ? `Your lead — set the ${g.mustLead}` : 'Your lead — any tile')
    : stuck ? (g.boneyard > 0 ? 'No play — draw from the boneyard' : 'No play — pass') : picked ? 'Fits both ends — pick one' : 'Your turn — tap a tile';
  else status = `${nameOf(st, g.turn)}'s turn`;

  const lastText = g.last && g.phase === 'playing'
    ? `${g.last.seat === seat ? 'You' : nameOf(st, g.last.seat)} ${g.last.kind === 'play' ? `played ${g.last.tile}` : g.last.kind === 'pass' ? 'passed' : `drew ${g.last.drew}${g.last.passed ? ' and passed' : ''}`}`
    : '';

  return (
    <div className={`gx-sp gx-sp--panel gx-sp--game gx-dm ${myTurn ? 'is-my-turn' : ''}`} role="region" aria-label="Dominoes game">
      <div className="gx-sp-head">
        <div className="gx-sp-score">
          {g.sides.map((s, i) => (
            <span key={i} className={`gx-sp-team ${i === mySide ? 'is-us' : ''}`}>
              {g.mode === 'partners' ? (i === mySide ? 'Us' : 'Them') : s[0] === seat ? 'You' : nameOf(st, s[0])} <b>{g.scores[i]}</b>
            </span>
          ))}
          <small className="gx-sp-note">to {g.target}{g.boneyard > 0 ? ` · boneyard ${g.boneyard}` : ''}</small>
        </div>
        <div className="gx-sp-head-actions">
          <HelpButton def={def} onHelp={onHelp} compact />
          <button className="gx-sp-btn gx-sp-btn--ghost" onClick={() => leaveTable(pid)}>Leave</button>
        </div>
      </div>

      {/* The line of play, with its two open ends as tap targets. */}
      <div className="gx-dm-board">
        <button className={`gx-dm-end ${pickedEnds.includes('left') ? 'is-target' : ''}`} disabled={!pickedEnds.includes('left') || entry.busy}
          onClick={() => picked && play(picked, 'left')} aria-label={e ? `Left end, ${e[0]}` : 'Left end'}>
          <small>Left</small><b>{e ? e[0] : '–'}</b>
        </button>
        <div className="gx-dm-line" ref={strip} aria-label={`Line of play, ${g.line.length} tiles`}>
          {g.line.length === 0 ? <span className="gx-sp-note">Waiting for the lead…</span>
            : g.line.map((p, i) => (
              <span key={`${p.tile}-${i}`} className={`gx-dm-placed ${g.last?.kind === 'play' && g.last.tile === p.tile ? 'is-last' : ''}`}>
                <DominoSvg a={p.l} b={p.r} vertical={p.l === p.r} size={16} />
              </span>
            ))}
        </div>
        <button className={`gx-dm-end ${pickedEnds.includes('right') ? 'is-target' : ''}`} disabled={!pickedEnds.includes('right') || entry.busy}
          onClick={() => picked && play(picked, 'right')} aria-label={e ? `Right end, ${e[1]}` : 'Right end'}>
          <small>Right</small><b>{e ? e[1] : '–'}</b>
        </button>
      </div>

      {/* Who holds what */}
      <div className="gx-dm-players">
        {g.players.map((s) => (
          <span key={s} className={`gx-dm-player ${g.turn === s && g.phase === 'playing' && st.phase === 'playing' ? 'is-turn' : ''} ${g.mode === 'partners' && sideOf(g, s) === mySide ? 'is-team' : ''}`}>
            {s === seat ? 'You' : nameOf(st, s)}{s === partner ? ' ★' : ''} <b>{g.counts[s]}</b>
          </span>
        ))}
      </div>

      <div className="gx-dm-status">
        <span className={`gx-sp-status ${myTurn ? 'is-mine' : ''}`}>{status}</span>
        {myTurn && <TurnTimer deadline={turnDeadline(st)} />}
      </div>

      {st.phase === 'over' ? (
        <GameOverBlock {...props} summary={g.sides.map((_, i) => g.scores[i]).join(' – ')} />
      ) : stuck ? (
        <div className="gx-sp-actions">
          <button className="gx-sp-btn gx-sp-btn--primary gx-dm-big" disabled={entry.busy}
            onClick={() => void tableAction(pid, 'move', { move: { type: g.boneyard > 0 ? 'draw' : 'pass' } })}>
            {g.boneyard > 0 ? 'Draw' : 'Pass'}
          </button>
        </div>
      ) : null}

      {g.phase === 'playing' && hand.length > 0 && (
        <div className="gx-dm-hand" role="group" aria-label="Your tiles">
          {hand.map((t) => {
            const [a, b] = tilePips(t);
            const ok = fits(t).length > 0 && !entry.busy;
            return (
              <button key={t} className={`gx-dm-tile ${ok ? 'is-playable' : myTurn ? 'is-dim' : ''} ${picked === t ? 'is-picked' : ''}`}
                disabled={!ok} aria-label={`${a}-${b}${ok ? ', playable' : ''}`} aria-pressed={picked === t} onClick={() => tap(t)}>
                <DominoSvg a={a} b={b} vertical size={24} />
              </button>
            );
          })}
        </div>
      )}
      {g.phase !== 'playing' && g.lastHand && (
        <div className="gx-dm-reveal">
          {g.players.map((s) => (
            <span key={s} className="gx-dm-reveal-row">
              <span>{s === seat ? 'You' : nameOf(st, s)} · {g.lastHand!.pips[s]}</span>
              {g.lastHand!.reveal[s].map((t) => { const [a, b] = tilePips(t); return <DominoSvg key={t} a={a} b={b} vertical size={12} />; })}
            </span>
          ))}
        </div>
      )}
      {(lastText || st.note) && <span className="gx-sp-note gx-sp-log">{lastText || st.note}</span>}
      <PanelError entry={entry} />
    </div>
  );
}

export default DominoesOverlay;
